import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import AdminSalesLayout from '../../components/AdminSalesLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';

const Card = styled.div`
  background: white;
  border-radius: 8px;
  padding: 28px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06);
  margin-bottom: 24px;
  border: 1px solid #e5e7eb;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 24px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e5e7eb;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 32px;
  
  @media (max-width: 1400px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: ${(p) => p.$bg || '#ffffff'};
  border: 1px solid ${(p) => p.$borderColor || '#e5e7eb'};
  border-left: 4px solid ${(p) => p.$accentColor || '#3b82f6'};
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  }
`;

const StatLabel = styled.div`
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7280;
  margin-bottom: 8px;
  font-weight: 600;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 6px;
  line-height: 1.2;
`;

const StatChange = styled.div`
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  tbody tr {
    transition: background-color 0.15s ease;
    
    &:nth-child(even) {
      background-color: #f9fafb;
    }
    
    &:hover {
      background-color: #f3f4f6;
    }
  }
`;

const Th = styled.th`
  text-align: left;
  padding: 14px 16px;
  background-color: #f9fafb;
  border-bottom: 2px solid #e5e7eb;
  font-weight: 600;
  color: #374151;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Td = styled.td`
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 14px;
  color: #374151;
  vertical-align: middle;
`;

const Avatar = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #d1d5db;
  background: #f3f4f6;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;

const AvatarPlaceholder = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 16px;
  border: 2px solid #d1d5db;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;

const CounselorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CounselorDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const CounselorName = styled.div`
  font-weight: 600;
  color: #111827;
  font-size: 14px;
  line-height: 1.4;
`;

const CounselorRole = styled.div`
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
  margin-top: 2px;
`;

const ProgressBarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 200px;
`;

const ProgressBarTrack = styled.div`
  flex: 1;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background: ${(p) => {
    if (!p.$value || p.$value < 25) return 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)';
    if (p.$value < 50) return 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)';
    if (p.$value < 75) return 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)';
    return 'linear-gradient(90deg, #10b981 0%, #059669 100%)';
  }};
  width: ${(p) => Math.min(100, Math.max(0, p.$value || 0))}%;
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const ProgressLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  min-width: 45px;
  text-align: right;
`;

const currencyFormatter = new Intl.NumberFormat('en-LK', {
  style: 'currency',
  currency: 'LKR',
  maximumFractionDigits: 0,
});

const percentFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});

function formatCurrency(value) {
  const numeric = Number.isFinite(value) ? value : 0;
  return currencyFormatter.format(Math.round(numeric));
}

function formatPercent(value) {
  if (!Number.isFinite(value)) {
    return '—';
  }
  return `${percentFormatter.format(value)}%`;
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}



export default function AdminSalesDashboard() {
  const [overview, setOverview] = useState({
    monthLabel: '',
    totals: {
      targetAmount: 0,
      newIncome: 0,
      balance: 0,
      achievement: null,
    },
    counselors: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let ignore = false;

    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await axiosInstance.get('/admin/sales-dashboard/counselor-targets');

        if (ignore) {
          return;
        }

        setOverview({
          monthLabel: data?.monthLabel || '',
          totals: {
            targetAmount: Number(data?.totals?.targetAmount) || 0,
            newIncome: Number(data?.totals?.newIncome) || 0,
            balance: Number(data?.totals?.balance) || 0,
            achievement: Number.isFinite(data?.totals?.achievement) ? data.totals.achievement : null,
          },
          counselors: Array.isArray(data?.counselors) ? data.counselors : [],
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching counselor targets overview:', err);
        if (!ignore) {
          setError('Unable to load counselor targets right now.');
          setOverview((prev) => ({
            ...prev,
            counselors: [],
          }));
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    fetchDashboardData();

    return () => {
      ignore = true;
    };
  }, []);

  const topIncomeCounselor = useMemo(() => {
    if (!overview.counselors.length) {
      return null;
    }
    return overview.counselors.reduce((best, current) => {
      if (!best) {
        return current;
      }
      return current.newIncome > best.newIncome ? current : best;
    }, null);
  }, [overview.counselors]);

  const lowestAchievementCounselor = useMemo(() => {
    if (!overview.counselors.length) {
      return null;
    }
    return overview.counselors.reduce((worst, current) => {
      if (!Number.isFinite(current.achievement)) {
        return worst;
      }
      if (!worst || !Number.isFinite(worst.achievement)) {
        return current;
      }
      return current.achievement < worst.achievement ? current : worst;
    }, null);
  }, [overview.counselors]);

  return (
    <AdminSalesLayout title="Admin Sales Dashboard" currentSection="dashboard">
      <Grid>
        <StatCard $accentColor="#3b82f6" $bg="#ffffff" $borderColor="#e5e7eb">
          <StatLabel>Monthly Target</StatLabel>
          <StatValue>{formatCurrency(overview.totals.targetAmount)}</StatValue>
          <StatChange>{overview.monthLabel || 'Current Month'}</StatChange>
        </StatCard>
        
        <StatCard $accentColor="#10b981" $bg="#ffffff" $borderColor="#e5e7eb">
          <StatLabel>New Income</StatLabel>
          <StatValue>{formatCurrency(overview.totals.newIncome)}</StatValue>
          <StatChange>Balance {formatCurrency(overview.totals.balance)}</StatChange>
          {overview.totals.targetAmount > 0 && (
            <div style={{ marginTop: '12px' }}>
              <ProgressBarTrack>
                <ProgressBarFill $value={(overview.totals.newIncome / overview.totals.targetAmount) * 100} />
              </ProgressBarTrack>
            </div>
          )}
        </StatCard>
        
        <StatCard $accentColor="#f59e0b" $bg="#ffffff" $borderColor="#e5e7eb">
          <StatLabel>Remaining Target</StatLabel>
          <StatValue>{formatCurrency(overview.totals.balance)}</StatValue>
          <StatChange>
            {Number.isFinite(overview.totals.achievement) && overview.totals.achievement > 0
              ? `${formatPercent(overview.totals.achievement)} achieved`
              : 'Target not yet started'}
          </StatChange>
        </StatCard>
        
        <StatCard $accentColor="#8b5cf6" $bg="#ffffff" $borderColor="#e5e7eb">
          <StatLabel>Active Counselors</StatLabel>
          <StatValue>{overview.counselors.length}</StatValue>
          <StatChange>
            {overview.counselors.length > 0
              ? `Avg target ${formatCurrency(overview.totals.targetAmount / overview.counselors.length)} per counselor`
              : 'No active counselors'}
          </StatChange>
        </StatCard>
      </Grid>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', paddingBottom: '12px', borderBottom: '2px solid #e5e7eb' }}>
          <Title style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
            Counselor Performance Overview
          </Title>
          {overview.monthLabel && (
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {overview.monthLabel}
            </div>
          )}
        </div>
        {loading ? (
          <p style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>Loading...</p>
        ) : error ? (
          <p style={{ textAlign: 'center', padding: '20px', color: '#ef4444' }}>{error}</p>
        ) : overview.counselors.length === 0 ? (
          <p style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>No counselor targets recorded for this month.</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Counselor</Th>
                <Th style={{ textAlign: 'right' }}>Target</Th>
                <Th style={{ textAlign: 'right' }}>New Income</Th>
                <Th style={{ textAlign: 'right' }}>Balance</Th>
                <Th style={{ minWidth: '240px' }}>Achievement</Th>
              </tr>
            </thead>
            <tbody>
              {overview.counselors.map((counselor) => {
                const initials = getInitials(counselor.counselorName);
                
                return (
                  <tr key={counselor.staffId}>
                    <Td>
                      <CounselorInfo>
                        {counselor.imagePath ? (
                          <Avatar src={getImageURL(counselor.imagePath)} alt={counselor.counselorName || 'Counselor'} />
                        ) : (
                          <AvatarPlaceholder>{initials}</AvatarPlaceholder>
                        )}
                        <CounselorDetails>
                          <CounselorName>{counselor.counselorName || '—'}</CounselorName>
                          <CounselorRole>{counselor.positionName || counselor.counselorType || '—'}</CounselorRole>
                        </CounselorDetails>
                      </CounselorInfo>
                    </Td>
                    <Td style={{ textAlign: 'right', fontWeight: 600, color: '#111827' }}>{formatCurrency(counselor.targetAmount)}</Td>
                    <Td style={{ textAlign: 'right', color: '#10b981', fontWeight: 600 }}>{formatCurrency(counselor.newIncome)}</Td>
                    <Td style={{ textAlign: 'right', color: '#6b7280', fontWeight: 500 }}>{formatCurrency(counselor.balance)}</Td>
                    <Td>
                      <ProgressBarContainer>
                        <ProgressBarTrack>
                          <ProgressBarFill $value={counselor.achievement} />
                        </ProgressBarTrack>
                        <ProgressLabel>{formatPercent(counselor.achievement)}</ProgressLabel>
                      </ProgressBarContainer>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>
    </AdminSalesLayout>
  );
}
